/** Canonical web URLs — marketing at `/`, product only under `/app`. */

export const WEB_LANDING_PATH = '/';
export const WEB_PRIVACY_PATH = '/privacy';
export const WEB_TERMS_PATH = '/terms';
export const WEB_SUPPORT_PATH = '/support';
/** Public post-purchase page (Stripe success + Ads / Play “thank you” URL). */
export const WEB_THANKS_PATH = '/thanks';
export const WEB_EXPLORE_PATH = '/explore';
export const WEB_APP_ROOT = '/app';
export const WEB_HOME_PATH = '/app/home';
export const WEB_SPEND_PATH = '/app/spend';
export const WEB_WEALTH_PATH = '/app/wealth';
export const WEB_GROW_PATH = '/app/grow';
export const WEB_LOAN_PATH = '/app/loan';
export const WEB_JOIN_PATH = '/app/join';
export const WEB_SHARE_PATH = '/app/share';
export const WEB_DELETE_ACCOUNT_PATH = '/app/delete-account';
/** Old Play / footer URL — rewrite to the in-app path. */
export const WEB_DELETE_ACCOUNT_LEGACY_PATH = '/delete-account';
export const WEB_SETTINGS_PATH = '/app/settings';

export const WEB_BILLING_SUCCESS_PATH = WEB_THANKS_PATH;
export const WEB_BILLING_CANCEL_PATH = `${WEB_HOME_PATH}?billing=cancel`;
export const WEB_BILLING_PORTAL_PATH = `${WEB_HOME_PATH}?billing=portal`;

const MARKETING_PATHS = new Set([
  WEB_LANDING_PATH,
  WEB_PRIVACY_PATH,
  WEB_TERMS_PATH,
  WEB_SUPPORT_PATH,
]);

/** Tab / product prefixes that used to live at the domain root. */
export const LEGACY_PRODUCT_PREFIXES = [
  'home',
  'spend',
  'wealth',
  'grow',
  'loan',
  'join',
  'share',
] as const;

export function normalizeWebPathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function isMarketingPath(pathname: string): boolean {
  return MARKETING_PATHS.has(normalizeWebPathname(pathname));
}

export function isDeleteAccountPath(pathname: string): boolean {
  const path = normalizeWebPathname(pathname);
  return path === WEB_DELETE_ACCOUNT_PATH || path === WEB_DELETE_ACCOUNT_LEGACY_PATH;
}

/** Public pages Google / Play may index — includes in-app delete (auth lives under `/app`). */
export function isIndexablePath(pathname: string): boolean {
  return isMarketingPath(pathname) || isDeleteAccountPath(pathname);
}

export function isJoinPath(pathname: string): boolean {
  const path = normalizeWebPathname(pathname);
  return path === '/join' || path === WEB_JOIN_PATH || path.endsWith('/join');
}

export function isSharePath(pathname: string): boolean {
  const path = normalizeWebPathname(pathname);
  return path === '/share' || path === WEB_SHARE_PATH || path.endsWith('/share');
}

/**
 * Rewrite pre-`/app` bookmarks to the product prefix.
 * `/preview` is retired — send those visitors to the sample dashboard.
 */
export function rewriteLegacyWebPath(path: string): string {
  const [rawPath, search = ''] = path.split('?');
  const normalized = rawPath.replace(/^\//, '');
  const query = search ? `?${search}` : '';

  if (normalized === 'welcome') {
    return `/${query}`;
  }

  if (normalized === 'delete-account' || normalized.startsWith('delete-account/')) {
    return `${WEB_DELETE_ACCOUNT_PATH}${query}`;
  }

  if (normalized === 'preview' || normalized.startsWith('preview/')) {
    return `${WEB_EXPLORE_PATH}${query}`;
  }

  for (const prefix of LEGACY_PRODUCT_PREFIXES) {
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      return `${WEB_APP_ROOT}/${normalized}${query}`;
    }
  }

  return path.startsWith('/') ? path : `/${path}`;
}
